import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getQueue, removeFromQueue } from "@/lib/offline";
import { CloudOff, CloudUpload, CheckCircle2, AlertTriangle } from "lucide-react";

async function drainQueue(qc, setState) {
  const items = await getQueue();
  if (!items.length) { setState({ pending: 0, syncing: false }); return; }
  setState({ pending: items.length, syncing: true });

  // Post one by one so we can map local tmp ids to real server ids
  // (backend /api/sync accepts a batch but returns ordered results)
  try {
    const ops = items.map(({ id: _rowId, created_at: _c, entity_id, ...rest }) => ({
      ...rest,
      ...(entity_id ? { id: entity_id } : {}),
    }));
    const { data } = await api.post("/sync", ops);
    let conflictCount = 0;
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      const res = data.results[i];
      if (res?.ok) {
        await removeFromQueue(row.id);
      } else if (res?.conflict) {
        conflictCount++;
        // Keep the item in queue but mark seen? For MVP: drop, notify user server won.
        await removeFromQueue(row.id);
      } else {
        // Non-network hard error → drop to avoid infinite loop, notify user
        await removeFromQueue(row.id);
        toast.error(`Sync-Fehler: ${res?.error || "unbekannt"}`);
      }
    }
    qc.invalidateQueries();
    if (conflictCount > 0) {
      toast.warning(`${conflictCount} Konflikt${conflictCount > 1 ? "e" : ""} — die Serverversion wurde übernommen.`);
    } else {
      toast.success(`${items.length} Änderung${items.length > 1 ? "en" : ""} synchronisiert`);
    }
  } catch (e) {
    // Still offline or server error — keep queue and try later
  } finally {
    const remaining = await getQueue();
    setState({ pending: remaining.length, syncing: false });
  }
}

export default function SyncManager() {
  const qc = useQueryClient();
  const [online, setOnline] = useState(navigator.onLine);
  const [state, setState] = useState({ pending: 0, syncing: false });

  const refreshCount = useCallback(async () => {
    const q = await getQueue();
    setState((s) => ({ ...s, pending: q.length }));
  }, []);

  const trySync = useCallback(() => {
    if (navigator.onLine) drainQueue(qc, setState);
  }, [qc]);

  useEffect(() => {
    refreshCount();
    const on = () => { setOnline(true); trySync(); };
    const off = () => setOnline(false);
    const changed = () => { refreshCount(); trySync(); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("stellar:queue-changed", changed);
    trySync();
    const iv = setInterval(trySync, 30000);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("stellar:queue-changed", changed);
      clearInterval(iv);
    };
  }, [refreshCount, trySync]);

  if (online && state.pending === 0 && !state.syncing) return null;

  const { pending, syncing } = state;
  let cls = "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30";
  let Icon = CloudOff;
  let label = "Offline";
  if (online && syncing) {
    cls = "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30";
    Icon = CloudUpload;
    label = `Synchronisiere… (${pending})`;
  } else if (online && pending > 0) {
    cls = "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30";
    Icon = CloudUpload;
    label = `${pending} ausstehend`;
  } else if (!online && pending > 0) {
    Icon = AlertTriangle;
    label = `Offline · ${pending} ausstehend`;
  }

  return (
    <button
      onClick={trySync}
      data-testid="sync-badge"
      title="Klicken zum Synchronisieren"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs hover:opacity-80 ${cls}`}
    >
      <Icon className={`h-3.5 w-3.5 ${syncing ? "animate-pulse" : ""}`} />
      {label}
    </button>
  );
}
